import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Input, Pill, SkeletonText, useToast } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import type { BurnLevel, ProjectBudget } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { editHours, editMoney, hoursLabel, moneyLabel, parseHours, parseMoney } from "../lib/budget-format.js";
import "./project-budget-tab.css";

/**
 * The project's Budget tab: budget, spent, remaining and burn in hours for
 * everyone who can read the project, and in money for admins and the project's
 * owner or managers (the server omits cost for everyone else, so there is
 * nothing here to hide client-side).
 */

const PAGE = 20;
const LEVEL_TONE: Record<BurnLevel, PillTone> = { ok: "ok", warning: "warn", over: "danger" };
const LEVEL_LABEL: Record<BurnLevel, string> = { ok: "On budget", warning: "Over 80% used", over: "Over budget" };

function Burn({ label, percent, level }: { label: string; percent: number | null; level: BurnLevel }) {
  if (percent === null) return <p className="budget-burn__none">No {label.toLowerCase()} budget set.</p>;
  return (
    <div className="budget-burn">
      <div className="budget-burn__head">
        <span>{label}</span>
        <strong>{percent}%</strong>
      </div>
      <div
        className={`budget-burn__bar budget-burn__bar--${level}`}
        role="progressbar"
        aria-label={`${label} budget used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(percent, 100)}
      >
        <span style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  );
}

export function ProjectBudgetTab({ projectId, onChanged }: { projectId: string; onChanged?: () => void }) {
  const { toast } = useToast();
  const [data, setData] = useState<ProjectBudget | null>(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ hours: "", money: "", rate: "", currency: "" });

  const load = useCallback(async () => {
    try {
      setData(await api.get<ProjectBudget>(`/projects/${projectId}/budget?limit=${PAGE}&offset=${offset}`));
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not load the budget.", { tone: "danger" });
    } finally {
      setLoading(false);
    }
  }, [projectId, offset, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <SkeletonText lines={4} />;
  if (!data) return <EmptyState icon="insights" title="Budget unavailable" description="Try again in a moment." />;

  const money = data.money;
  const startEdit = () => {
    setForm({
      hours: editHours(data.budgetMinutes),
      money: editMoney(money?.budgetCents ?? null),
      rate: editMoney(money?.billableRateCents ?? null),
      currency: money?.currency ?? "",
    });
    setEditing(true);
  };

  const save = async () => {
    const budgetMinutes = parseHours(form.hours);
    const budgetCents = parseMoney(form.money);
    const billableRateCents = parseMoney(form.rate);
    const currency = form.currency.trim().toUpperCase();
    if (budgetMinutes === undefined || budgetCents === undefined || billableRateCents === undefined || !/^[A-Z]{3}$/.test(currency)) {
      toast("Check the numbers (hours and money as 12.5) and use a 3-letter currency code.", { tone: "danger" });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/budget`, { budgetMinutes, budgetCents, billableRateCents, currency });
      setEditing(false);
      await load();
      onChanged?.();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not save the budget.", { tone: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const worst = money?.level ?? data.level;

  return (
    <div className="budget-tab">
      <section className="budget-card" aria-label="Budget summary">
        <div className="budget-card__head">
          <h3>Budget</h3>
          <Pill tone={LEVEL_TONE[worst]}>{LEVEL_LABEL[worst]}</Pill>
          {data.canEdit && !editing && (
            <Button size="sm" variant="secondary" onClick={startEdit}>
              Edit budget
            </Button>
          )}
        </div>

        {editing ? (
          <div className="budget-form">
            <Input label="Hours budget" inputMode="decimal" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} hint="Leave empty for none." />
            <Input label="Money budget" inputMode="decimal" value={form.money} onChange={(e) => setForm({ ...form, money: e.target.value })} hint="Leave empty for none." />
            <Input label="Billable rate per hour" inputMode="decimal" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} hint="Overrides each person's own rate on this project." />
            <Input label="Currency" value={form.currency} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value })} hint="Three letters, e.g. EUR." />
            <div className="budget-form__actions">
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={() => void save()} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="budget-grid">
            <div>
              <h4>Hours</h4>
              <dl className="budget-figures">
                <dt>Budget</dt>
                <dd>{data.budgetMinutes === null ? "Not set" : hoursLabel(data.budgetMinutes)}</dd>
                <dt>Spent</dt>
                <dd>{hoursLabel(data.spentMinutes)}</dd>
                <dt>Remaining</dt>
                <dd>{data.remainingMinutes === null ? "-" : hoursLabel(data.remainingMinutes)}</dd>
              </dl>
              <Burn label="Hours" percent={data.burnPercent} level={data.level} />
            </div>
            {money && (
              <div>
                <h4>Money ({money.currency})</h4>
                <dl className="budget-figures">
                  <dt>Budget</dt>
                  <dd>{money.budgetCents === null ? "Not set" : moneyLabel(money.budgetCents, money.currency)}</dd>
                  <dt>Spent</dt>
                  <dd>{moneyLabel(money.spentCents, money.currency)}</dd>
                  <dt>Remaining</dt>
                  <dd>{money.remainingCents === null ? "-" : moneyLabel(money.remainingCents, money.currency)}</dd>
                </dl>
                <Burn label="Money" percent={money.burnPercent} level={worstOf(money.burnPercent)} />
                {money.unratedMinutes > 0 && (
                  <p className="budget-note">{hoursLabel(money.unratedMinutes)} was logged by people with no hourly rate and counts as zero cost.</p>
                )}
              </div>
            )}
          </div>
        )}
        {!money && <p className="budget-note">Cost figures are visible to admins and the project's owner and managers.</p>}
      </section>

      <section className="budget-card" aria-label="Spent per person">
        <h3>Per person</h3>
        {data.byPerson.length === 0 ? (
          <p className="budget-note">No time logged yet.</p>
        ) : (
          <table className="budget-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Hours</th>
                {money && <th>Cost</th>}
              </tr>
            </thead>
            <tbody>
              {data.byPerson.map((p) => (
                <tr key={p.userId}>
                  <td>{p.userName ?? "Unknown"}</td>
                  <td>{hoursLabel(p.minutes)}</td>
                  {money && <td>{moneyLabel(p.cents ?? 0, money.currency)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.byPersonTotal > PAGE && (
          <div className="budget-pager">
            <Button size="sm" variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </Button>
            <span>
              {offset + 1}-{Math.min(offset + PAGE, data.byPersonTotal)} of {data.byPersonTotal}
            </span>
            <Button size="sm" variant="ghost" disabled={offset + PAGE >= data.byPersonTotal} onClick={() => setOffset(offset + PAGE)}>
              Next
            </Button>
          </div>
        )}
      </section>

      <section className="budget-card" aria-label="Spent per month">
        <h3>Per month</h3>
        {data.byMonth.length === 0 ? (
          <p className="budget-note">No time logged yet.</p>
        ) : (
          <table className="budget-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Hours</th>
                {money && <th>Cost</th>}
              </tr>
            </thead>
            <tbody>
              {data.byMonth.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td>{hoursLabel(m.minutes)}</td>
                  {money && <td>{moneyLabel(m.cents ?? 0, money.currency)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** The bar colour for the money burn alone (the pill beside the title shows the worse of both). */
function worstOf(percent: number | null): BurnLevel {
  if (percent === null) return "ok";
  return percent >= 100 ? "over" : percent >= 80 ? "warning" : "ok";
}
